/**
 * @component ry-alert
 * @category  feedback
 * @summary   Inline alert message (Fomantic UI `.ui.message` equivalent). Persistent block-level callout. Use `<ry-toast>` for transient overlay notifications and `<ry-alert>` for in-flow page messages.
 *
 * Reference: Fomantic UI's message module. Variants map to Fomantic's
 * positive/negative/info/warning. ry-alert keeps `success`/`error` as
 * canonical and accepts `positive`/`negative` as aliases via theme CSS.
 *
 * @attribute variant      'info' | 'success' | 'warning' | 'error'?   default="info"   Visual variant.
 * @attribute dismissible  boolean   default=false   When present, renders a close button that calls `dismiss()`.
 *
 * @property  state        'closed'?    Set when `dismiss()` is called; `data-ry-state="closed"` drives the fade-out before removal. Read-only.
 *
 * @method    dismiss()    Begin the close transition. State becomes "closed"; element is removed from DOM ~200ms later. Fires `ry:close` immediately.
 *
 * @event ry:beforeclose
 *        detail: {}
 *        fires:  at the top of `dismiss()` — close-button click or `el.dismiss()` — before any state change
 *        cancelable: preventDefault() keeps the alert in place; `ry:close` does not fire
 *
 * @event ry:close
 *        detail: {}
 *        fires:  immediately at the start of dismiss() — BEFORE the element is removed
 *
 * @keyboard  None on the alert itself. The optional close button is a `<button>` and inherits Enter/Space.
 *
 * @state     `data-ry-state` of `"closed"` when dismissing (transient — element is removed shortly after).
 *
 * @content   Any HTML. The original innerHTML is lifted into a `[data-ry-target="content"]` slot at setup time.
 *
 * @dom
 *   ry-alert[variant][data-variant][role="alert"]
 *     > div.ry-alert__content    [data-ry-target="content"]   {original innerHTML}
 *     > button.ry-alert__close   [data-ry-target="close"] [close]   (only when [dismissible])
 *
 * @gotcha  Without `[dismissible]`, no close button is rendered AND the user cannot remove the alert via UI. Call `el.dismiss()` programmatically, or `el.remove()` if you don't need the fade-out.
 *
 * @gotcha  The original `innerHTML` is consumed and replaced. Use `el.querySelector('[data-ry-target="content"]').innerHTML` to read the rendered content; don't reach for `el.innerHTML` after mount.
 *
 * @gotcha  `dismiss()` fires `ry:close` immediately but waits ~200ms before removing. Listeners can rely on the event firing first; tests that need to assert removal must wait.
 *
 * @gotcha  `role="alert"` is applied at setup. Screen readers announce the content immediately on insertion. Use `<ry-alert>` only when the message warrants interruption — for informational text use a plain `<div>`.
 *
 * @example basic
 * ```html
 * <ry-alert>Default info alert.</ry-alert>
 * ```
 *
 * @example all-variants
 * ```html
 * <ry-alert variant="info">Information</ry-alert>
 * <ry-alert variant="success">Saved successfully</ry-alert>
 * <ry-alert variant="warning">Please review before submitting</ry-alert>
 * <ry-alert variant="error">Network request failed</ry-alert>
 * ```
 *
 * @example dismissible
 * ```html
 * <ry-alert variant="warning" dismissible>
 *   You have unsaved changes.
 * </ry-alert>
 * ```
 *
 * @example with-rich-content
 * ```html
 * <ry-alert variant="info" dismissible>
 *   <strong>New feature.</strong> Check out the <a href="/docs">docs</a> for details.
 * </ry-alert>
 * ```
 *
 * @tutorial mount-and-content
 * ```js
 * // Mounting lifts the original innerHTML into the content slot.
 * const el = mount('<ry-alert variant="success"><strong>Saved!</strong></ry-alert>');
 * const content = el.querySelector('[data-ry-target="content"]');
 * await expectEqual(content.innerHTML.trim(), '<strong>Saved!</strong>', 'innerHTML preserved');
 * await expectAttr(el, 'role', 'alert', 'role="alert" set');
 * await expectAttr(el, 'data-variant', 'success', 'variant reflected as data-variant');
 * ```
 *
 * @tutorial dismissible-renders-close-button
 * ```js
 * const el = mount('<ry-alert variant="warning" dismissible>Heads up</ry-alert>');
 * const closeBtn = el.querySelector('[data-ry-target="close"]');
 * await expectEqual(!!closeBtn, true, 'close button rendered when dismissible');
 * await expectAttr(closeBtn, 'aria-label', 'Dismiss');
 * ```
 *
 * @tutorial non-dismissible-omits-close-button
 * ```js
 * // Wrong assumption: every alert has a close button. By default, alerts
 * // are persistent; the close button is opt-in via [dismissible].
 * const el = mount('<ry-alert>Permanent</ry-alert>');
 * await expectEqual(el.querySelector('[data-ry-target="close"]'), null, 'no close button by default');
 * ```
 *
 * @tutorial close-click-dismisses
 * ```js
 * const el = mount('<ry-alert dismissible>x</ry-alert>');
 * const closeBtn = el.querySelector('[data-ry-target="close"]');
 * await expectEvent(el, 'ry:close', () => closeBtn.click());
 * await expectAttr(el, 'data-ry-state', 'closed', 'state transitions to closed');
 * ```
 *
 * @tutorial programmatic-dismiss-fires-then-removes
 * ```js
 * // Wrong assumption: dismiss() removes synchronously. It does NOT.
 * // ry:close fires immediately while the element is still attached;
 * // the element is removed ~200ms later.
 * const el = mount('<ry-alert>x</ry-alert>');
 * const fired = [];
 * el.addEventListener('ry:close', () => fired.push({
 *   attached: el.isConnected,
 *   state: el.getAttribute('data-ry-state'),
 * }));
 * el.dismiss();
 * await expectEqual(fired.length, 1, 'ry:close fires');
 * await expectEqual(fired[0].attached, true, 'still attached when ry:close fires');
 * await expectEqual(fired[0].state, 'closed', 'state="closed" when ry:close fires');
 * await sleep(250);
 * await expectEqual(el.isConnected, false, 'removed after ~200ms');
 * ```
 *
 * @tutorial beforeclose-veto
 * ```js
 * // preventDefault() on ry:beforeclose keeps the alert in place — the
 * // close button and el.dismiss() share the same guard.
 * const el = mount('<ry-alert dismissible>x</ry-alert>');
 * const closed = [];
 * el.addEventListener('ry:close', () => closed.push(1));
 * el.addEventListener('ry:beforeclose', (e) => e.preventDefault(), { once: true });
 * el.querySelector('[data-ry-target="close"]').click();
 * await expectEqual(el.getAttribute('data-ry-state'), null, 'vetoed dismiss leaves state untouched');
 * await expectEqual(closed.length, 0, 'ry:close did not fire');
 * el.dismiss();
 * await expectEqual(el.getAttribute('data-ry-state'), 'closed', 'without the veto, dismiss() works');
 * await expectEqual(closed.length, 1, 'ry:close fired once');
 * ```
 *
 * @tutorial default-variant-info
 * ```js
 * // Wrong assumption: missing variant renders a chrome-less generic alert.
 * // Default variant is "info".
 * const el = mount('<ry-alert>Hi</ry-alert>');
 * await expectAttr(el, 'data-variant', 'info', 'default variant is info');
 * ```
 *
 * @see ry-toast
 * @see ry-modal
 */

import { RyElement } from '../core/ry-element.js';

export class RyAlert extends RyElement {
  setup(): void {
    // Transform minimal markup
    this.#transformMarkup();

    // Dismissible (use [close] attribute)
    const closeBtn = this.$('[close]');
    if (closeBtn) {
      this.on(closeBtn, 'click', () => this.dismiss());
    }
  }

  #transformMarkup(): void {
    // Check if already transformed
    if (this.$('[data-ry-target="content"]')) return;

    const variant = this.getAttribute('variant') ?? 'info';
    const dismissible = this.hasAttribute('dismissible');
    const content = this.innerHTML;

    // data-ry-target for JS queries, class for CSS styling
    this.innerHTML = `
      <div data-ry-target="content" class="ry-alert__content">${content}</div>
      ${dismissible ? '<button data-ry-target="close" class="ry-alert__close" close aria-label="Dismiss">&times;</button>' : ''}
    `;

    this.setAttribute('role', 'alert');
    this.setAttribute('data-variant', variant);
  }

  dismiss(): void {
    if (!this.emit('beforeclose')) return;

    // Write directly — RyElement's state setter no-ops when newState ===
    // oldState, and 'closed' is the default state getter value.
    this.dataset.ryState = 'closed';

    // Remove after animation
    setTimeout(() => {
      this.remove();
    }, 200);

    this.emit('close');
  }
}

customElements.define('ry-alert', RyAlert);
